import { faList, faSearch } from '@fortawesome/pro-solid-svg-icons';
import type { ComponentProps } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { catchAll } from '@sorare/routing';

import { Sport, StoryState } from '#src/__generated__/globalTypes.ts';
import { Cards } from '#src/assets/icons/Cards.tsx';
import { Dropdown } from '#src/atoms/dropdowns/Dropdown/index.tsx';
import { Auction } from '#src/atoms/icons/Auction.tsx';
import { Binoculars } from '#src/atoms/icons/Binoculars.tsx';
import { Collection } from '#src/atoms/icons/Collection.tsx';
import { HomeIcon } from '#src/atoms/icons/HomeIcon.tsx';
import { InstantBuy } from '#src/atoms/icons/InstantBuy.tsx';
import { LiveRadar } from '#src/atoms/icons/LiveRadar.tsx';
import { Market } from '#src/atoms/icons/Market.tsx';
import { More } from '#src/atoms/icons/More.tsx';
import { MyCards } from '#src/atoms/icons/MyCards.tsx';
import { Packs } from '#src/atoms/icons/Packs.tsx';
import { PlayFootball } from '#src/atoms/icons/PlayFootball.tsx';
import { PlayMLB } from '#src/atoms/icons/PlayMLB.tsx';
import { PlayNBA } from '#src/atoms/icons/PlayNBA.tsx';
import { ProIcon } from '#src/atoms/icons/Pro.tsx';
import { Quickplay } from '#src/atoms/icons/Quickplay.tsx';
import { Scores } from '#src/atoms/icons/Scores.tsx';
import { StarBall } from '#src/atoms/icons/StarBall/index.tsx';
import { TopPlayers } from '#src/atoms/icons/TopPlayers.tsx';
import { Transfer } from '#src/atoms/icons/Transfer.tsx';
import { Trophy } from '#src/atoms/icons/Trophy.tsx';
import { Vault } from '#src/atoms/icons/Vault.tsx';
import { WinterRound } from '#src/atoms/icons/WinterRound.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { NavItem } from '#src/components/navigation/NavItem/index.tsx';
import {
  ANY_SPORT_HOME,
  ANY_SPORT_MARKET,
  ANY_SPORT_MARKET_SCOUT,
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
  ANY_SPORT_MARKET_WATCHLISTS,
  ANY_SPORT_MY_CARDS,
  ANY_SPORT_MY_CARDS_CARDS,
  ANY_SPORT_MY_CARDS_COLLECTIONS,
  ANY_SPORT_MY_CARDS_VAULT,
  ANY_SPORT_PLAY,
  ANY_SPORT_PLAY_EVENTTYPE_ARENA,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS,
  ANY_SPORT_PLAY_QUICK_PLAY,
  ANY_SPORT_PLAY_SERIES,
  ANY_SPORT_SCORES,
  ANY_SPORT_SCORES_MATCHES,
  ANY_SPORT_SCORES_TOP_PLAYERS,
} from '#src/constants/__generated__/routes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useLifecycle } from '#src/hooks/useLifecycle.ts';
import { useUnclaimedMarketTasksCount } from '#src/hooks/users/useUnclaimedMarketTasksCount.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import {
  glossary,
  navLabels,
  scoutingCenter,
  transferMarket,
} from '#src/lib/glossary.ts';
import {
  generateSportPath,
  matchAnySportPath,
} from '#src/lib/routing/generateSportPath.ts';
import { ClickMarketHoverMenuButton_Target } from '#src/protos/events/so5/shared/events.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import { MoreNavItems } from './MoreNavItems/index.tsx';
import { SearchNavItem } from './SearchNavItem/index.tsx';

const Root = styled(Horizontal).attrs({
  gap: 0,
})`
  width: 100%;
  position: relative;
  justify-content: space-between;

  & > * {
    flex: 1;
  }
  .seriesNav & {
    width: unset;
    background: var(--c-nl-500);
    backdrop-filter: blur(8px);
    border-radius: 64px;
    padding: var(--unit) var(--double-unit);
    justify-content: center;
    align-items: center;
    & > * {
      width: unset;
      min-width: calc(17 * var(--vw));
      padding: 0 var(--unit);
    }
  }
  @media ${laptopAndAbove} {
    flex-direction: column;
    align-items: stretch;
  }
`;
const DropdownContent = styled(Horizontal)`
  background-color: var(--c-black);
  padding: var(--half-unit);
`;

const SubNavRoot = styled(Vertical).attrs({ gap: 0.5 })`
  border-radius: var(--intermediate-unit);
  background-color: var(--c-black);
`;

const ICONS = {
  [Sport.BASEBALL]: PlayMLB,
  [Sport.FOOTBALL]: PlayFootball,
  [Sport.NBA]: PlayNBA,
} as const;

const itemsToClaim = defineMessage({
  id: 'NavItems.playItemsToClaim',
  defaultMessage: '{count, plural, one {# item} other {# items}} to claim',
});

type ItemProps = ComponentProps<typeof NavItem> & { subItems?: ItemProps[] };

const NavItemWithSubNav = ({
  item,
  level = 0,
}: {
  item: ItemProps;
  level?: number;
}) => {
  const isLaptopAndAbove = useIsLaptop();

  const { subItems, to, ...mainItemProps } = item;
  if (isLaptopAndAbove && !!subItems?.length) {
    if (level > 0) {
      return (
        <>
          <NavItem to={to} {...mainItemProps} />
          <SubNavRoot
            style={{ paddingLeft: `calc(${level} * var(--quadruple-unit))` }}
          >
            {subItems.map(subNavItem => (
              <NavItemWithSubNav
                key={subNavItem.label}
                item={subNavItem}
                level={level + 1}
              />
            ))}
          </SubNavRoot>
        </>
      );
    }
    return (
      <Dropdown
        forceDesktopBehavior
        triggerOnHover
        side="right"
        gap={24}
        label={({ open, ...otherProps }) => (
          <menu {...otherProps} type="menu">
            <li>
              <NavItem to={to} {...mainItemProps} />
            </li>
          </menu>
        )}
      >
        {({ closeDropdown }) => (
          <DropdownContent>
            <SubNavRoot onClick={closeDropdown}>
              {subItems.map(subNavItem => (
                <NavItemWithSubNav
                  key={subNavItem.label}
                  item={subNavItem}
                  level={level + 1}
                />
              ))}
            </SubNavRoot>
          </DropdownContent>
        )}
      </Dropdown>
    );
  }

  return <NavItem to={to} {...mainItemProps} />;
};

type Props = { sport: Sport; sidebarCollapsed?: boolean };
export const NavItems = ({ sport, sidebarCollapsed }: Props) => {
  const { formatMessage } = useIntl();
  const isLaptopAndAbove = useIsLaptop();
  const navItemsVariant = isLaptopAndAbove ? 'horizontal' : 'vertical';
  const { lifecycle } = useLifecycle();
  const { so5 } = useConfigContext();
  const { currentUser } = useCurrentUserContext();
  const { pathname } = useLocation();
  const {
    flags: {
      useQuickPlay = {
        [Sport.BASEBALL]: false,
        [Sport.NBA]: false,
        [Sport.FOOTBALL]: false,
      },
      enableArenaV2 = false,
    },
  } = useFeatureFlags();
  const track = useEvents();

  const seriesNav = useSeriesNav();
  const PlayIcon = ICONS[sport];

  const showLiveDot = so5?.currentManagerHasLiveGames;

  const myCardsRewardsCount = currentUser?.unclaimedSo5Rewards?.length || 0;

  const unclaimedMarketTasksCount = useUnclaimedMarketTasksCount();

  const playBadgeCount = {
    [Sport.FOOTBALL]:
      so5?.myAvailableFixtureStory?.aasmState === StoryState.READY ? 1 : 0,
    [Sport.BASEBALL]: 0,
    [Sport.NBA]: 0,
  }[sport];

  const marketBadgeCount = {
    [Sport.FOOTBALL]: unclaimedMarketTasksCount,
    [Sport.BASEBALL]: 0,
    [Sport.NBA]: unclaimedMarketTasksCount,
  }[sport];

  const onClickFn = (t: ClickMarketHoverMenuButton_Target) => {
    return () => {
      track('Click Market Hover Menu Button', {
        sport,
        target: t,
      });
    };
  };

  const iconSize = isLaptopAndAbove ? 2 : 2.5;

  const getMainPlayLink = () => {
    if (seriesNav) {
      return generateSportPath(ANY_SPORT_PLAY_SERIES, {
        sport,
      });
    }
    return generateSportPath(
      lifecycle?.lastVisitedPlayTab?.[sport] === 'quickPlay' &&
        sport === Sport.NBA &&
        useQuickPlay[sport]
        ? ANY_SPORT_PLAY_QUICK_PLAY
        : ANY_SPORT_PLAY,
      {
        sport,
      }
    );
  };

  return (
    <Root>
      {!!isLaptopAndAbove && !!sidebarCollapsed && (
        <SearchNavItem
          variant={navItemsVariant}
          icon={() => <FontAwesomeIcon icon={faSearch} size="sm" />}
          label={formatMessage(navLabels.search)}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}

      {!seriesNav && (
        <NavItem
          variant={navItemsVariant}
          icon={() => <HomeIcon size={iconSize} />}
          label={formatMessage(navLabels.home)}
          to={generateSportPath(ANY_SPORT_HOME, { sport })}
          sidebarCollapsed={sidebarCollapsed}
          badge={{
            content: currentUser?.myUnclaimedTasksCount,
          }}
          enableContextModeNavigation
        />
      )}
      <NavItemWithSubNav
        item={{
          variant: navItemsVariant,
          icon: ({ isActive }) => (
            <PlayIcon size={iconSize} active={isActive} />
          ),
          label: formatMessage(navLabels.play),
          to: getMainPlayLink(),
          sidebarCollapsed,
          enableContextModeNavigation: true,
          enabledStoredLastNavigation: true,
          badge: playBadgeCount > 0 ? { content: playBadgeCount } : undefined,
          showLiveDot: sport !== Sport.NBA && showLiveDot,
          subItems: !seriesNav
            ? [
                ...(useQuickPlay[sport]
                  ? [
                      {
                        icon: () => <Quickplay size={2} />,
                        label: formatMessage(glossary.quickPlay),
                        to: generateSportPath(ANY_SPORT_PLAY_QUICK_PLAY, {
                          sport,
                        }),
                      },
                    ]
                  : []),
                {
                  icon: () => <ProIcon />,
                  label: formatMessage(navLabels.pro),
                  to: generateSportPath(ANY_SPORT_PLAY, {
                    sport,
                  }),
                  controlledActive: !!matchAnySportPath(
                    catchAll(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS),
                    pathname
                  ),
                },
                ...(enableArenaV2
                  ? [
                      {
                        icon: () => <Trophy size={2} />,
                        label: formatMessage(navLabels.leaderboards),
                        to: generateSportPath(ANY_SPORT_PLAY_EVENTTYPE_ARENA, {
                          sport,
                          params: {
                            eventType: 'classic',
                          },
                        }),
                      },
                    ]
                  : []),
                ...(currentUser?.anyUserSportProfile?.proUnlocked
                  ? [
                      {
                        icon: () => <WinterRound />,
                        label: formatMessage(navLabels.set),
                        to: generateSportPath(ANY_SPORT_PLAY_SERIES, {
                          sport,
                        }),
                      },
                    ]
                  : []),
              ]
            : [],
        }}
      />
      {!seriesNav && (
        <NavItemWithSubNav
          item={{
            variant: navItemsVariant,
            icon: ({ isActive }) => (
              <Scores size={iconSize} active={isActive} />
            ),
            label: formatMessage(navLabels.scores),
            to: generateSportPath(ANY_SPORT_SCORES, {
              sport,
            }),
            sidebarCollapsed,
            enableContextModeNavigation: true,
            subItems: [
              {
                icon: () => <LiveRadar size={2} />,
                label:
                  sport === Sport.FOOTBALL
                    ? formatMessage(navLabels.matches)
                    : formatMessage(glossary.games),
                to: generateSportPath(ANY_SPORT_SCORES_MATCHES, {
                  sport,
                }),
              },
              {
                icon: () => <TopPlayers size={2} />,
                label: formatMessage(glossary.topPlayers),
                to: generateSportPath(ANY_SPORT_SCORES_TOP_PLAYERS, {
                  sport,
                }),
              },
            ],
          }}
        />
      )}
      <NavItemWithSubNav
        item={{
          variant: navItemsVariant,
          icon: ({ isActive }) => <Market size={iconSize} active={isActive} />,
          label: formatMessage(navLabels.transferMarket),
          to: generateSportPath(ANY_SPORT_MARKET, {
            sport,
          }),
          enableContextModeNavigation: true,
          sidebarCollapsed,
          badge: {
            content: marketBadgeCount,
          },
          ...(!seriesNav && {
            subItems: [
              {
                icon: () => <StarBall width={16} height={16} />,
                label: formatMessage(glossary.explore),
                to: generateSportPath(ANY_SPORT_MARKET, {
                  sport,
                }),
                end: true,
                subItems: [
                  {
                    icon: () => <Auction />,
                    label: formatMessage(navLabels.auctions),
                    to: generateSportPath(ANY_SPORT_MARKET_SHOP_AUCTIONS, {
                      sport,
                    }),
                    onClick: onClickFn(
                      ClickMarketHoverMenuButton_Target.AUCTIONS
                    ),
                  },
                  {
                    icon: () => <InstantBuy size={2} />,
                    label: formatMessage(transferMarket.instantBuy),
                    to: generateSportPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY, {
                      sport,
                    }),
                    onClick: onClickFn(
                      ClickMarketHoverMenuButton_Target.INSTANT_BUY
                    ),
                  },
                  {
                    icon: () => <Transfer />,
                    label: formatMessage(transferMarket.transfer),
                    to: generateSportPath(ANY_SPORT_MARKET_SHOP_MANAGER_SALES, {
                      sport,
                    }),
                    onClick: onClickFn(
                      ClickMarketHoverMenuButton_Target.MANAGER_SALES
                    ),
                  },
                  {
                    icon: () => <Packs />,
                    label: formatMessage(navLabels.packs),
                    to: `${generateSportPath(ANY_SPORT_MARKET, {
                      sport,
                    })}?rarity=common`,
                    badge: {
                      content: marketBadgeCount,
                    },
                    end: true,
                  },
                ],
              },
              {
                icon: () => <Binoculars />,
                label: formatMessage(scoutingCenter.scout),
                to: generateSportPath(ANY_SPORT_MARKET_SCOUT, {
                  sport,
                }),
              },
              {
                icon: () => <FontAwesomeIcon icon={faList} size="1x" />,
                label: formatMessage(scoutingCenter.watchlists),
                to: generateSportPath(ANY_SPORT_MARKET_WATCHLISTS, {
                  sport,
                }),
              },
            ],
          }),
        }}
      />
      <NavItemWithSubNav
        item={{
          variant: navItemsVariant,
          icon: ({ isActive }) => <MyCards size={iconSize} active={isActive} />,
          label: formatMessage(navLabels.myCards),
          to: generateSportPath(ANY_SPORT_MY_CARDS, {
            sport,
          }),
          enableContextModeNavigation: true,
          enabledStoredLastNavigation: true,
          sidebarCollapsed,
          badge: {
            content: myCardsRewardsCount,
            ariaLabel: formatMessage(itemsToClaim, {
              count: myCardsRewardsCount,
            }),
          },
          subItems: [
            {
              icon: () => <Cards />,
              label: formatMessage(navLabels.gallery),
              to: generateSportPath(ANY_SPORT_MY_CARDS_CARDS, {
                sport,
              }),
            },
            {
              icon: () => <Collection size={2} />,
              label: formatMessage(navLabels.collections),
              to: generateSportPath(ANY_SPORT_MY_CARDS_COLLECTIONS, {
                sport,
              }),
            },
            !seriesNav && {
              icon: () => <Vault size={2} />,
              label: formatMessage(navLabels.vault),
              to: generateSportPath(ANY_SPORT_MY_CARDS_VAULT, {
                sport,
              }),
            },
          ].filter(Boolean),
        }}
      />
      {!!isLaptopAndAbove && (
        <Dropdown
          forceDesktopBehavior
          triggerOnHover
          side="right"
          gap={24}
          label={({ open, ...otherProps }) => (
            <menu {...otherProps} type="menu">
              <li>
                <NavItem
                  icon={() => <More size={2} />}
                  label={formatMessage(navLabels.more)}
                  sidebarCollapsed={sidebarCollapsed}
                />
              </li>
            </menu>
          )}
        >
          {({ closeDropdown }) => (
            <DropdownContent>
              <MoreNavItems onClick={closeDropdown} />
            </DropdownContent>
          )}
        </Dropdown>
      )}
    </Root>
  );
};
